import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Clock, Users, Calendar } from 'lucide-react';
import { API } from '../lib/api';

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

export default function ReportsPage() {
  const [hoursByProject, setHoursByProject] = useState<any[]>([]);
  const [utilization, setUtilization] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDate(d);
  });
  const [endDate, setEndDate] = useState(() => formatDate(new Date()));

  useEffect(() => {
    loadReports();
  }, [startDate, endDate]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const [projectData, utilData, projectsData] = await Promise.all([
        API.getHoursByProject(startDate),
        API.getUtilization(),
        API.getProjects(),
      ]);
      setHoursByProject(projectData);
      setUtilization(utilData);
      setProjects(projectsData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const totalHours = hoursByProject.reduce((sum, p) => sum + (p.hours || 0), 0);
  const avgUtilization = utilization.length > 0
    ? utilization.reduce((sum, u) => sum + (u.utilization || 0), 0) / utilization.length
    : 0;

  const getProjectName = (projectId: string) => {
    const project = projects.find((p) => p.projectId === projectId);
    return project?.name || projectId;
  };

  const presetRanges = [
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
  ];

  const setPresetRange = (days: number) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Reports</h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {presetRanges.map((range) => (
                <button
                  key={range.days}
                  onClick={() => setPresetRange(range.days)}
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
                >
                  {range.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Hours</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{totalHours.toFixed(1)}</p>
              <p className="text-sm text-gray-500 mt-1">Across all projects</p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-lg">
              <Clock className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Projects</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{hoursByProject.length}</p>
              <p className="text-sm text-gray-500 mt-1">Currently tracked</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Team Utilization</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {(avgUtilization * 100).toFixed(0)}%
              </p>
              <p className="text-sm text-gray-500 mt-1">Average billable ratio</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-6">
          <BarChart3 className="w-6 h-6 text-indigo-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Hours by Project</h3>
        </div>
        {hoursByProject.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No project data available for this period</p>
        ) : (
          <div className="space-y-4">
            {hoursByProject.map((project) => (
              <div key={project.projectId} className="flex items-center">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-900">{getProjectName(project.projectId)}</span>
                    <span className="text-sm font-medium text-gray-900">{project.hours.toFixed(1)}h</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all"
                      style={{ width: `${totalHours > 0 ? (project.hours / totalHours) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center mb-6">
          <Users className="w-6 h-6 text-indigo-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Team Utilization</h3>
        </div>
        {utilization.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No utilization data available</p>
        ) : (
          <div className="space-y-4">
            {utilization.map((user) => (
              <div key={user.userId} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-indigo-700">
                      user.userId?.charAt(0).toUpperCase() || 'U'
                    </span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">{user.userId}</p>
                    <p className="text-sm text-gray-500">
                      {user.billable?.toFixed(1) || 0}h billable / {user.total?.toFixed(1) || 0}h total
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {((user.utilization || 0) * 100).toFixed(0)}%
                  </p>
                  <p className="text-xs text-gray-500">utilization</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
